import React from 'react';
import i18n from 'i18n';
import _ from 'lodash';
import * as actionTypes from 'constants/action-types';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import axios from 'axios/axios-base';
import { MockService, MockServiceAction, PriorityMode, TRANSACTIONAL, UpdateConfig } from 'types/rest/mockServiceTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ActionCreator, AnyAction } from 'redux';
import { ApplicationState } from 'store/rootReducer';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { POLL_INTERVAL_MS, POLL_NUM_OF_RETRIES, pollTrackingService } from 'axios/axios-polling';
import MixpanelService from 'utils/mixpanelService';
import {
  CREATE_SERVICE_SNAPSHOT,
  DELETE_MOCK_SERVICE,
  DEPLOY_MOCK_SERVICE,
  MOCK_SERVICE_BULK_OPERATION,
  RECONFIURE_MOCK_SERVICE,
  STOP_MOCK_SERVICE,
} from 'utils/mixpanelEvents';
import { generateDate } from 'utils/dateUtil';
import {
  collapseAllTemplates,
  createMockServiceTemplate,
  getAssetsForMockServiceFromTemplate,
  getMockServiceTemplates,
} from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { cacheControlHeaders, getDifference, getErrorMessage } from 'utils/miscUtil';
import {
  getAndProcessMetricsForMockServices,
  getBulkPartialFailureErrorMessage,
  getDefaultLocation,
  getLocationId,
  hasEndpointChanged,
  initiateTrackingsForMockServices,
  isDirty,
  isLocationScalable,
  isOplLocation,
  isUpgradeMockAvailableFromListOfMocks,
  sanitizeMockPatchDto,
} from 'utils/mockServiceUtils';
import {
  AccountPlan,
  BlazeMeterApiResponse,
  BlazemeterCredentials,
  BlazemeterUserConfig,
} from 'types/rest/blazeMeterApiResponse';
import { action } from 'typesafe-actions';
import { getAccountPlan, getActiveWorkspaceId, getBulkLimit, isPrivateCloud } from 'utils/bzmUserConfigUtil';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import {
  fetchTransactionsForShuttlePane,
  getAssetsForMockService,
  mockServiceDetailTabChanged,
  reloadTransactionShuttle,
  setAssetBundle,
} from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { UiState } from 'store/ui/uiReducer';
import { CraneLocation } from 'types/rest/locationTypes';
import { MsgComponent, Queue, Topic, Transaction } from 'types/rest/assetCatalogTypes';
import store from '../../configureStore';
import { getModelComplexity, isComplexityOverThreshold } from '../../../utils/blazeDataUtils';
import { getDataModelForService } from '../../dataModel/dataModelActions';
import { bulkMockServiceStageUpdate, checkIfMockServiceExistInWorkspaceThunk } from '../mockServiceActions';
import { confirm } from '../../../components/ui/modal/BlazeConfirmationModal';
import { BadgeEnum } from '../../../components/badge/Badge';
import acApi from 'axios/ac/acApi';
import { MockServiceBulkAction } from '../../../views/MockServices/Tabs/MockServices/MockServices';
import { TRANSACTIONAL_LOCATIONS_ID_SUFFIX } from '../../../utils/locationUtil';
import { addTracking, removeTracking, updateTracking } from '../../trackings/trackingsReducer';
import { ServiceMockTrackingDto, Tracking } from '../../trackings/trackingsTypes';
import { Link } from 'react-router-dom';

const finishedTrackingDelay = 750;

/**
 * This method refreshes list of MockServices from the backend. The list is mix of drafts and original virtual services.
 * Results are filtered by type and serviceId which are taken from redux store on a backend side and additional filtering
 * is applied on results from backend on ui side (search by name)
 **/
export const getMockServiceListThunk: ActionCreator<ThunkAction<
  any,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    try {
      dispatch({ type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: true } });

      //Add show upgradable mocks filter
      const isUpgradableShow = getState().ui.showUpgradableMockOnly;

      //Add location filter
      const filter = getState().mockservices.filter;

      //Add Virtual Service filter if set
      const selectedService = filter && filter.serviceId ? filter.serviceId : undefined;

      // Build query params
      const params = {
        limit: -1,
        sort: '-draftId,-serviceMockId',
        serviceId: selectedService && selectedService > 0 ? selectedService.toString() : undefined,
        shipId: filter && filter.shipId ? filter.shipId.split(TRANSACTIONAL_LOCATIONS_ID_SUFFIX)[0] : undefined,
        status: filter && filter.status ? filter.status : undefined,
        onlyWarnings: isUpgradableShow,
      };

      const response = await axios.get(`/workspaces/{workspaceId}/ui/service-mocks`, {
        headers: cacheControlHeaders,
        params: params,
      });

      const mockServices = response.data.result as MockService[];

      // Initiate trackings for virtual services in configuring state
      // Check if the list was already loaded (by checking the load time stamp)
      // If the time stamp is empty, we are loading it for very first time, hence we will restart the trackings
      // otherwise tracking are not going to be initiated for virtual services
      const applicationState = getState() as ApplicationState;
      if (_.isNil(applicationState.mockservices.loadTimeStamp)) {
        await initiateTrackingsForMockServices(mockServices);
      }

      dispatch({
        type: actionTypes.VIEW_MOCK_SERVICES,
        payload: mockServices,
      });

      //Get Metric data for displayed virtual services
      getAndProcessMetricsForMockServices(mockServices, getActiveWorkspaceId());
    } catch (err) {
      dispatch({ type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: false } });
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.cannotLoadMockServices') + ' ' + getErrorMessage(err),
        ),
      );
    }
  };
};

/**
 * Toggles expansion of the row in virtual service table.
 * @param mockServiceId
 */
export const toggleExpandedMockServiceRow = (mockServiceId: number | null) =>
  action(actionTypes.MOCK_SERVICE_TOGGLE_EXPAND_ROW, mockServiceId);

/**
 * Expands virtual service row by id.
 * @param mockServiceId
 */
export const expandMockServiceRow = (mockServiceId: number) =>
  action(actionTypes.MOCK_SERVICE_EXPAND_ITEM, mockServiceId);

export const showLoading = () => {
  return { type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: true } };
};

export const updateMockServicesListCount = (count: number) => {
  return { type: actionTypes.UPDATE_MOCK_SERVICES_LIST_COUNT, payload: { count: count } };
};

export const toggleMockServiceInAction = (
  serviceMockId: number,
  metaData?: { inProgressAction?: MockServiceAction },
) => {
  return { type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: serviceMockId, metaData };
};

export const resolveBadges = (mockService: MockService) => {
  const badges: BadgeEnum[] = [];
  // add redirect to live flag
  if (mockService.noMatchingRequestPreference !== 'return404') {
    badges.push(BadgeEnum.LIVE);
  }
  // add delayed flag
  if (
    mockService.responseDelay !== undefined &&
    mockService.responseDelay !== null &&
    mockService.responseDelay.type !== 'none'
  ) {
    badges.push(BadgeEnum.DELAYED);
  }
  // add stateful flag
  if (mockService.metadata?.badges?.STATE_UPDATE === true) {
    badges.push(BadgeEnum.STATEFUL);
  }
  // add JVM heap override flag
  if (mockService.jvmMemoryOverrideEnabled === true && isOplLocation(mockService.harborId, mockService.shipId)) {
    badges.push(BadgeEnum.MEM_OVERRIDE);
  }
  /*// add webhook flag
  if (mockService.metadata.badges?.WEBHOOK === true) {
    badges.push(BADGE_WEBHOOK);
  }
  // add httpCall flag
  if (mockService.metadata.badges?.HTTP_CALL === true) {
    badges.push(BADGE_HTTP_CALL);
  }*/
  return badges;
};

/**
 * Function which updates mock service metadata object with deployment status. Called
 * during the polling.
 * @param response
 * @param mockServiceId
 * @param dispatch
 */
const updateMockServiceDeploymentStage = (response: any, mockServiceId: number, dispatch: any, getState: any) => {
  try {
    if (!_.has(response, 'data.result.data.serviceMockTrackingDtos')) {
      return;
    }

    //Get current mock service object
    const mockService = _.find(getState().mockservices.dataList, item => item.id === mockServiceId);
    const deploymentStatus = _.get(response.data.result.data.serviceMockTrackingDtos[0], 'trackingDto.data.stage');
    const msUpdate = _.cloneDeep(mockService);
    msUpdate.metaData.trackingStatus = deploymentStatus; //Current deploystatus

    //Set starting deploy timestamp
    if (deploymentStatus === 'STARTING') {
      msUpdate.metadata.stages['STARTING'] = new Date().toISOString();
    }

    //Dispatch change of status if it is different from previous
    if (mockService.metaData.trackingStatus !== deploymentStatus) {
      dispatch(mockServiceChanged(msUpdate));
    }
  } catch (e) {
    console.error(e);
  }
};

export const importTopicsAndQueues: ActionCreator<ThunkAction<any, ApplicationState, null, MockServiceActionTypes>> = (
  mockService: MockService,
  queues: Queue[],
  topics: Topic[],
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>) => {
    try {
      const brokerConfig = mockService.brokerConfig;
      if (brokerConfig != undefined) {
        brokerConfig.topics = [
          ...brokerConfig.topics,
          ...topics.filter(
            topic =>
              !brokerConfig.topics.some(
                existingTopic => existingTopic.name?.toLowerCase() === topic.name?.toLowerCase(),
              ),
          ),
        ];
        brokerConfig.queues = [
          ...brokerConfig.queues,
          ...queues.filter(
            queues =>
              !brokerConfig.queues.some(
                existingQueue => existingQueue.name?.toLowerCase() === queues.name?.toLowerCase(),
              ),
          ),
        ];
        const patchMockService = {
          id: mockService.id,
          brokerConfig: brokerConfig,
        };

        await acApi.mockServices.patchMockService(mockService.id, patchMockService);

        // Update the mock service in the store
        dispatch(mockServiceChanged({ ...mockService, brokerConfig: brokerConfig }));
        dispatch(showToastNotification(ToastType.Success, 'Import', 'Items successfully imported'));
      } else {
        throw new Error('not BrokerConfig found on this MockService, cannot import topics and queues');
      }
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, 'Import Error', error.message));
    }
  };
};

export const testMessagingMockConnection = async (mockService: MockService) => {
  // Validate virtual service location exists
  if (mockService.type === TRANSACTIONAL && (_.isEmpty(mockService.harborId) || _.isEmpty(mockService.shipId))) {
    throw new Error(i18n.t('mockServices:errorMsgs.noLocation'));
  }
  const testResponse = await acApi.mockServices.testMessagingConnection(mockService.id);
  if (!testResponse || !testResponse.data || !testResponse.data.result || !testResponse.data.result.trackingId) {
    throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
  }

  try {
    const trackingId = testResponse.data.result.trackingId;
    // Poll for up to ~10 minutes (200 retry attempts with 3 second timeout between each)
    // until the virtual service is deployed
    const trackingResult = await pollTrackingService(trackingId, 200, 3000);
    const errorMsg = _.get(trackingResult, 'data.result.errors');
    let formattedErrors = null;
    if (!_.isEmpty(errorMsg)) {
      formattedErrors = errorMsg.join('\n\n');
    }
    const internalErrorMsg = _.get(
      trackingResult,
      'data.result.data.serviceMockTrackingDtos[0].trackingDto.data.testConnectionResult.errorMessage',
    );
    let formattedInternalErrors = null;
    if (!_.isEmpty(internalErrorMsg)) {
      formattedInternalErrors = internalErrorMsg.join('\n\n');
    }
    if (formattedErrors || formattedInternalErrors) {
      const combinedErrors = [formattedErrors, formattedInternalErrors].filter(Boolean).join('\n\n');
      throw new Error(combinedErrors);
    }

    const topics = _.get(
      trackingResult,
      'data.result.data.serviceMockTrackingDtos[0].trackingDto.data.testConnectionResult.topics',
    );
    const queues = _.get(
      trackingResult,
      'data.result.data.serviceMockTrackingDtos[0].trackingDto.data.testConnectionResult.queues',
    );

    const formatedTopics = topics
      ? (topics.map((topic: any, index: number) => ({ id: index + 1, name: topic })) as MsgComponent[])
      : [];
    const formatedQueues = queues
      ? (queues.map((queues: any, index: number) => ({ id: index + 1, name: queues })) as MsgComponent[])
      : [];

    return { queues: formatedQueues, topics: formatedTopics };
  } catch (error) {
    throw error;
  }
};

export const deployMockService: ActionCreator<ThunkAction<any, ApplicationState, null, MockServiceActionTypes>> = (
  mockService: MockService,
  credentials?: BlazemeterCredentials,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any) => {
    MixpanelService.track(DEPLOY_MOCK_SERVICE(mockService), true);

    if (!isPrivateCloud()) {
      checkBlazeDataComplexity(mockService);
    }

    let serviceMockId = mockService.id;

    //If virtual service is draft, save it before run
    try {
      if (mockService.isDraft) {
        const original = await dispatch(mergeDraft(mockService));
        serviceMockId = original.id;
      }

      // Validate virtual service location exists
      if (mockService.type === TRANSACTIONAL && (_.isEmpty(mockService.harborId) || _.isEmpty(mockService.shipId))) {
        throw new Error(i18n.t('mockServices:errorMsgs.noLocation'));
      }

      //Create tracking object
      const tracking: Tracking = {
        entityId: serviceMockId,
        action: 'DEPLOY',
        entityType: 'MOCK_SERVICE',
      };
      dispatch(addTracking(tracking));

      //Show the in progress message
      dispatch({
        type: actionTypes.TOGGLE_IN_PROGRESS,
        serviceMockId: serviceMockId,
        mockServiceMeta: {
          isMerging: true,
        },
        metaData: {
          inProgressAction: 'DEPLOY',
        },
      });

      // Append credentials to the call
      let credentialParams = null;
      if (credentials) {
        credentialParams = createCredentialParameters(credentials, getState);
      }

      const deployResponse = await acApi.mockServices.deploy(serviceMockId, {
        headers: cacheControlHeaders,
        params: credentialParams,
      });

      if (
        !deployResponse ||
        !deployResponse.data ||
        !deployResponse.data.result ||
        !deployResponse.data.result.trackingId
      ) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }

      const trackingId = deployResponse.data.result.trackingId;
      const workSpaceId = getActiveWorkspaceId();

      //Update tracking object
      dispatch(
        updateTracking({
          ...tracking,
          trackingDto: {
            trackingId: trackingId,
            status: 'RUNNING',
          },
        }),
      );

      //Expand mock service if not expanded
      const expanded = getExpandedMockService();
      if (_.isNil(expanded) || (expanded && expanded.id !== serviceMockId)) {
        dispatch(toggleExpandedMockServiceRow(mockService.id));
      }

      // Poll for up to ~10 minutes (600 retry attempts with 1 second timeout between each)
      // until the virtual service is deployed
      const trackingResult = await pollTrackingService(trackingId, POLL_NUM_OF_RETRIES, POLL_INTERVAL_MS, data =>
        updateMockServiceDeploymentStage(data, serviceMockId, dispatch, getState),
      );
      const errorMsg = _.get(trackingResult, 'data.result.errors');
      const warningMsg = _.get(trackingResult, 'data.result.warnings');
      if (!_.isEmpty(errorMsg)) {
        throw new Error(errorMsg);
      }

      //Wait a second before we fetch newly created object from backend
      await setTimeout(() => _.noop(), finishedTrackingDelay);

      //Get the newly deployed virtual service
      const deployedMockService = await getServiceMock(serviceMockId, workSpaceId);

      if (deployedMockService.status !== 'FAILED') {
        let isEndpointDifferent = false;

        //Compare endpoint only for TXN type
        if (deployedMockService.type && deployedMockService.type === TRANSACTIONAL) {
          if (warningMsg && warningMsg[0]) {
            dispatchWarningMessage(warningMsg[0].toString());
          }

          isEndpointDifferent = hasEndpointChanged(mockService, deployedMockService);
        }

        dispatchDeploymentSuccesMessage(deployedMockService, isEndpointDifferent);
      } else {
        throw new Error(i18n.t('errorMsgs.networkProblem'));
      }

      //Refresh the whole list if user has not switched workspace
      if (workSpaceId === getActiveWorkspaceId()) {
        dispatch(getMockServiceListThunk());
      }
    } catch (error) {
      dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: mockService.id });
      dispatchDeploymentErrorMessage(mockService, error);

      // When error happens, make sure object in the view is updated with current status from backend
      await dispatch(updateServiceMockThunk(serviceMockId));
    } finally {
      //Remove tracking
      dispatch(removeTracking({ type: 'MOCK_SERVICE', id: serviceMockId }));
    }
  };
};

/**
 * Gets single virtual service from backend and does transformations (the ui dto is different from virtual service dto)
 */
export const getServiceMock = async (serviceMockId: number, workspaceId: number) => {
  const mockServiceResponse = await axios.get(`/workspaces/${workspaceId}/service-mocks/${serviceMockId}`, {
    headers: cacheControlHeaders,
  });

  const mockService = _.get(mockServiceResponse, 'data.result');
  if (_.isNil(mockService)) {
    throw new Error('Virtual Service could not be loaded');
  }
  mockService.isDraft = false;
  mockService.serviceMockId = mockService.id;
  return mockService;
};

/**
 * Update virtual service in local redux store with its current version from backend.
 * @param serviceMockId
 */
export const updateServiceMockThunk = (mockServiceId: number): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    const mockServiceResponse = await axios.get(`/workspaces/{workspaceId}/service-mocks/${mockServiceId}`, {
      headers: cacheControlHeaders,
    });

    const mockService: MockService = _.get(mockServiceResponse, 'data.result');
    if (mockService !== null) {
      dispatch({
        type: actionTypes.SAVE_OR_UPDATE_MOCK_SERVICE,
        payload: {
          tempId: mockServiceId,
          newMockService: mockService,
        },
      });
    }
  };
};

/**
 * Shows danger alert with message.
 * @param mockService being processed
 * @param error
 */
export const dispatchDeploymentErrorMessage = (mockService: MockService, error: any) => {
  let errorTitle: string = i18n.t('mockServices:errorMsgs.failedToDeployMockService', { name: mockService.name });
  let errorBody: any;

  //In case message is string
  if (_.isString(error)) {
    //Put the string to the error body right away
    errorBody = error;

    //In case of Invalid SV credentials, show custom content with link to credentials page
    if (_.includes(error.toLowerCase(), 'Invalid SV credentials'.toLowerCase())) {
      errorTitle = '';
      const blazeUserConfig: BlazemeterUserConfig | null = (store.getState() as ApplicationState).blazeUserConfig
        .blazeUserConfig;
      const blazeBaseUrl: string = blazeUserConfig?.config.blazeMeterAppUrl;
      const accountId: number | undefined = blazeUserConfig?.user.activeAccountId;
      const workspaceId: number | undefined = blazeUserConfig?.user.activeWorkspaceId;
      const bzmCredentialsPageUrl = `${blazeBaseUrl}/#/settings/accounts/${accountId}/workspaces/${workspaceId}/apm-credentials`;
      const invalidCredentials: string = i18n.t('mockServices:errorMsgs.invalidSvCredentials');

      errorBody = (
        <p>
          {invalidCredentials}
          <a className="alert-link" href={bzmCredentialsPageUrl}>
            {i18n.t('mockServices:errorMsgs.invalidSvCredentialsPage')}
          </a>
        </p>
      );
    }
  } else {
    errorBody = _.get(error, 'response.data.error', error.message);
  }

  let isMockDeleteOrNotFound = false;
  if (
    !_.isEmpty(errorBody) &&
    (_.includes(errorBody.toLowerCase(), 'Failed to find Virtual Service'.toLowerCase()) ||
      _.includes(errorBody.toLowerCase(), 'MockService deleted'.toLowerCase()))
  ) {
    isMockDeleteOrNotFound = true;
  }
  // Dispatch error msg
  if (!isMockDeleteOrNotFound) {
    store.dispatch(showToastNotification(ToastType.Danger, errorTitle, errorBody));
  }
};

/**
 * Show success message when deployment of the virtual service was success.
 * Gets mockServicesExpiration from User Account Plan -> If its -1 then deploy Virtual Service with no Warning.
 * Else, display toast showing when will the Virtual Service expire.
 *
 * @param mockService in process
 * @param dispatch redux dispatch
 * @param getState redux state
 * @param endpointChanged to show warning that endpoint changed for virtual services.
 */
export const dispatchDeploymentSuccesMessage = (mockService: MockService, endpointChanged: boolean) => {
  const accountPlan: AccountPlan = getAccountPlan();
  const mockServicesExpiration = accountPlan.mockServicesExpiration;

  const successMsgForTransactions =
    mockService.type === TRANSACTIONAL && endpointChanged
      ? i18n.t('mockServices:successMsgs.deploySuccessMsgChangedEndpoint', { name: mockService.name })
      : i18n.t('mockServices:successMsgs.deploySuccessMsg', { name: mockService.name });

  const successMsg =
    mockService.type === TRANSACTIONAL
      ? successMsgForTransactions
      : i18n.t('mockServices:successMsgs.svDeploySuccessMsg');

  if (mockServicesExpiration === -1) {
    store.dispatch(showToastNotification(ToastType.Success, successMsg));
  } else {
    if (mockService.type === TRANSACTIONAL) {
      store.dispatch(
        showToastNotification(
          ToastType.Warning,
          '',
          i18n.t('mockServices:successMsgs.deploySuccessMsgWithExpirationWarning', {
            mockServicesExpiration: mockServicesExpiration / 60,
          }),
        ),
      );
    }
  }
};

/**
 * Show warning message if port already in use for docker OPL
 * @param message
 */
export const dispatchWarningMessage = (message: string) => {
  store.dispatch(showToastNotification(ToastType.Warning, '', message, 9000));
};

/**
 * Creates sv credentials for http request. If we are running in private cloud env we append creds directly
 * to the request base64 encoded.
 * @param credentials blaze credentials
 * @param getState
 */
const createCredentialParameters = (credentials: BlazemeterCredentials, getState: any) => {
  let svCredentials = { credentialsId: credentials.id };

  // Append credentials to request in case of private cloud is on
  if (isPrivateCloud(getState().blazeUserConfig.blazeUserConfig)) {
    svCredentials = _.assign({}, svCredentials, {
      username: btoa(credentials.credentials.username),
      password: btoa(credentials.credentials.password),
    });
  }

  return svCredentials;
};

/**
 * Creates a template object from existing service.
 * @param mockService
 */
export const createTemplateFromServiceThunk = (
  mockService: MockService,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<any, null, AnyAction>, getState: any) => {
    MixpanelService.track(CREATE_SERVICE_SNAPSHOT());

    const pageFilter = getState().ui.pageFilter;
    const selectedServiceId: number = pageFilter.serviceId;

    // Override virtual service template with virtual service data
    const newSMTemplate: MockServiceTemplate = Object.assign({}, mockService, { original: null });
    newSMTemplate.name = mockService.name + '-template_' + generateDate();
    if (newSMTemplate.name.length > 250) {
      newSMTemplate.name = newSMTemplate.name.substring(0, 250);
    }

    //Get Assets and add them to teplate DTO
    try {
      const currentMS = mockService.isDraft
        ? await acApi.mockServiceDrafts.getById(mockService.id)
        : await acApi.mockServices.getById(mockService.id);

      newSMTemplate.assets = _.get(currentMS, 'data.result.assets');
      newSMTemplate.mockServiceTransactions = _.get(currentMS, 'data.result.mockServiceTransactions');
      newSMTemplate.mockServiceRecordings = _.get(currentMS, 'data.result.mockServiceRecordings');
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve virtual service certificates'));
    }

    // Get rid of not needed atributes
    delete newSMTemplate.id;

    //Create message
    const msg = (
      <p className={'m-0'}>
        {i18n.t('mockServices:successMsgs.templateCreated', {
          name: newSMTemplate.name,
        })}{' '}
        <br />
        <Link className={'mt-2'} to="/mock-services/templates">
          Go to Virtual Service Templates
        </Link>
      </p>
    );

    createMockServiceTemplate(newSMTemplate, mockService.serviceId)
      .then(() => {
        // Show success
        dispatch(
          showToastNotification(ToastType.Success, i18n.t('mockServices:successMsgs.createSuccessMsgTempl'), msg, 6000),
        );

        // Refresh list of services
        dispatch(getMockServiceTemplates(selectedServiceId, true));
      })
      .catch((error: any) => {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('errorMsgs.unexpectedError'),
            i18n.t('mockServices:errorMsgs.cantSaveTemplate', {
              reason: error.response ? error.response.data.error : 'Something went wrong.',
            }),
          ),
        );
        console.log(error);
      });
  };
};

/**
 * Creates new Virtual Service object from Template.
 */
export const createServiceFromTemplateThunk: ActionCreator<ThunkAction<
  any,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (smTemplate: MockServiceTemplate): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    let smName = 'MockService-' + generateDate() + ' from ' + smTemplate.name;
    if (smName.length > 250) {
      smName = smName.substring(0, 250);
    }

    // Assign default location to new template
    const locations: CraneLocation[] = getState().mockservices.locData;
    const defaultLocation: CraneLocation | null = getDefaultLocation(locations);
    const mockServiceToCreate = {
      id: null,
      serviceId: smTemplate.serviceId,
      name: smName,
      description: smTemplate.description,
      harborId: defaultLocation ? defaultLocation.harborId : null,
      shipId: defaultLocation ? defaultLocation.shipId : null,
      appliedTemplateId: smTemplate.isDraft ? smTemplate.serviceMockTemplateId : smTemplate.id,
      replicas: smTemplate.replicas ? smTemplate.replicas : 1,
      endpointPreference: 'HTTPS',
      mockServiceTransactions: smTemplate.mockServiceTransactions,
      noMatchingRequestPreference: smTemplate.noMatchingRequestPreference,
      thinkTime: smTemplate.thinkTime,
      liveSystemHost: smTemplate.liveSystemHost,
      liveSystemPort: -1,
      type: TRANSACTIONAL.toString(),
      dataSettings: smTemplate.dataSettings,
      responseDelay: smTemplate.responseDelay,
      assets: smTemplate.assets,
      configurationId: smTemplate.configurationId,
      proxy: smTemplate.proxy,
      priorityMode: smTemplate.priorityMode ? smTemplate.priorityMode : PriorityMode.default,
      messagingProtocol: smTemplate.messagingProtocol,
      brokerConfig: smTemplate.brokerConfig,
      httpRunnerEnabled: smTemplate.httpRunnerEnabled,
      messagingRunnerEnabled: smTemplate.messagingRunnerEnabled,
      rateLimit: smTemplate.rateLimit,
      rateBurst: smTemplate.rateBurst,
      authSettings: smTemplate.authSettings,
    };

    //Virtual Service doesn't exist, so let's create it
    const saveResponse = await axios.post(`/workspaces/{workspaceId}/service-mocks`, mockServiceToCreate);

    // Dispatch Virtual Service action which appends this item to the beginning of the list,
    dispatch({
      type: actionTypes.CREATE_MOCK_SERVICE,
      payload: {
        newMockService: saveResponse.data.result,
        showNewItem: true,
      },
    });

    const msg = (
      <p className={'m-0'}>
        {i18n.t('mockServices:successMsgs.saveSuccessMsg', { name: mockServiceToCreate.name })} <br />
        <Link to="/mock-services" className={'mt-4'}>
          Go to Virtual Services
        </Link>
      </p>
    );
    dispatch(showToastNotification(ToastType.Success, 'Successfully created Virtual Service', msg, 6000));
  };
};

/**
 * Merges draft to the backend
 * @param mockService
 */
export const mergeDraft: any = (
  mockService: MockService,
  credentials?: BlazemeterCredentials,
  updateConfig?: UpdateConfig,
): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any) => {
    if (mockService.isDraft !== true) {
      new Error('Not a draft, cannot merge');
    }

    const uiState = getState().ui as UiState;
    // Merge this draft
    dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: mockService.id });

    try {
      const savedMockService = await invokeMergeAndSwapDraft(mockService.id, updateConfig);
      dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: savedMockService.id });
      if (!mockService.metaData.isDeployed) {
        if (uiState.pageFilter.serviceId !== 0 && uiState.pageFilter.serviceId !== savedMockService.serviceId) {
          dispatch(
            showToastNotification(
              ToastType.Warning,
              i18n.t('mockServices:successMsgs.updateSuccessMsg'),
              i18n.t('mockServices:warnMsgs.createMockServiceButFilteredOut', { name: savedMockService.name }),
            ),
          );
        } else {
          dispatch(
            showToastNotification(
              ToastType.Success,
              i18n.t('mockServices:successMsgs.updateSuccessMsg', { name: mockService.name }),
            ),
          );
        }
      }

      // Call reconfigure, if virtual service is running
      if (savedMockService.status === 'RUNNING') {
        await dispatch(reconfigureMockServiceThunk(savedMockService, credentials, updateConfig));
      }
      if (uiState.pageFilter.serviceId !== 0 && uiState.pageFilter.serviceId !== savedMockService.serviceId) {
        dispatch(getMockServiceListThunk());
      }
      return savedMockService;
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', '');
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('mockServices:errorMsgs.failedToUpdateMockService', { name: mockService.name }),
          errorBody,
        ),
      );
      dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: mockService.id });
      return null;
    }
  };
};

/**
 * Calls virtual service re-configuration
 */
export const reconfigureMockServiceThunk: any = (
  mockService: MockService,
  credentials?: BlazemeterCredentials,
  updateConfig?: UpdateConfig,
): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any) => {
    const serviceMockId = mockService.id;

    dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: mockService.id });

    if (!isPrivateCloud()) {
      checkBlazeDataComplexity(mockService);
    }

    try {
      //Configure the virtual service
      MixpanelService.track(RECONFIURE_MOCK_SERVICE(mockService), true);

      let params = null;
      if (credentials) {
        params = createCredentialParameters(credentials, getState);
      }

      if (!_.isNil(updateConfig)) {
        params = {
          ...params,
          keepBlazeData: !_.isNil(updateConfig.keepBlazeData) ? updateConfig.keepBlazeData : undefined,
        };
      }

      const configureResponse = await axios.get(`/workspaces/{workspaceId}/service-mocks/${serviceMockId}/configure`, {
        headers: cacheControlHeaders,
        params: params,
      });
      if (
        !configureResponse ||
        !configureResponse.data ||
        !configureResponse.data.result ||
        !configureResponse.data.result.trackingId
      ) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      const trackingId = configureResponse.data.result.trackingId;

      // Poll for up to ~10 minutes (600 retry attempts with 1 second timeout between each)
      // until the virtual service is configured
      await pollTrackingService(trackingId);

      //Wait a while before we fetch newly created object from backend
      await setTimeout(() => _.noop(), finishedTrackingDelay);

      //Get the reconfigured virtual service
      const configuredServiceResponse = await axios.get(`/workspaces/{workspaceId}/service-mocks/${serviceMockId}`, {
        headers: cacheControlHeaders,
      });
      if (configuredServiceResponse.status === 200) {
        if (configuredServiceResponse.data.result.status !== 'FAILED') {
          dispatch(
            showToastNotification(
              ToastType.Success,
              i18n.t('mockServices:successMsgs.updateSuccessMsg', { name: mockService.name }),
            ),
          );
        } else {
          dispatch(
            showToastNotification(
              ToastType.Danger,
              i18n.t('errorMsgs.networkProblem'),
              i18n.t('mockServices:errorMsgs.failedToUpdateMockService', {
                name: mockService.name,
              }),
            ),
          );
        }
      } else {
        throw new Error(i18n.t('mockServices:errorMsgs.errorOnConfig'));
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('mockServices:errorMsgs.failedToUpdateMockService', { name: mockService.name }),
          errorMsg,
        ),
      );
    } finally {
      //Refresh list of virtual services
      dispatch(getMockServiceListThunk());
    }
  };
};

/**
 * Calls mock upgrade for one virtual service
 */
export const upgradeMockServiceThunk: any = (
  mockService: MockService,
  credentials?: BlazemeterCredentials,
  updateConfig?: UpdateConfig,
): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any) => {
    const serviceMockId = mockService.id;

    dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: mockService.id });

    try {
      //Configure the virtual service
      MixpanelService.track(RECONFIURE_MOCK_SERVICE(mockService), true);

      let params = null;
      if (credentials) {
        params = createCredentialParameters(credentials, getState);
      }

      if (!_.isNil(updateConfig)) {
        params = {
          ...params,
          keepBlazeData: !_.isNil(updateConfig.keepBlazeData) ? updateConfig.keepBlazeData : undefined,
        };
      }

      const configureResponse = await axios.get(`/workspaces/{workspaceId}/service-mocks/${serviceMockId}/upgrade`, {
        headers: cacheControlHeaders,
        params: params,
      });
      if (
        !configureResponse ||
        !configureResponse.data ||
        !configureResponse.data.result ||
        !configureResponse.data.result.trackingId
      ) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      const trackingId = configureResponse.data.result.trackingId;

      // Poll for up to ~10 minutes (600 retry attempts with 1 second timeout between each)
      // until the virtual service is configured
      await pollTrackingService(trackingId);

      //Wait a while before we fetch newly created object from backend
      await setTimeout(() => _.noop(), finishedTrackingDelay);

      //Get the reconfigured virtual service
      const configuredServiceResponse = await axios.get(`/workspaces/{workspaceId}/service-mocks/${serviceMockId}`, {
        headers: cacheControlHeaders,
      });
      if (configuredServiceResponse.status === 200) {
        if (configuredServiceResponse.data.result.status !== 'FAILED') {
          const result = configuredServiceResponse.data.result;
          if (
            result &&
            result.metadata &&
            result.metadata.warnings &&
            !result.metadata.warnings.upgradeAvailable &&
            result.metadata.version === getState().blazeUserConfig.blazeUserConfig.config.version
          ) {
            const toast = showToastNotification(ToastType.Success, '', getSuccessToastBody(mockService.name), 6000);
            dispatch(toast);
          } else {
            dispatch(
              showToastNotification(
                ToastType.Danger,
                '',
                i18n.t('mockServices:errorMsgs.failedToUpgradeMockService', {
                  name: mockService.name,
                }),
              ),
            );
          }
        } else {
          dispatch(
            showToastNotification(
              ToastType.Danger,
              '',
              i18n.t('mockServices:errorMsgs.failedToUpgradeMockService', {
                name: mockService.name,
              }),
            ),
          );
        }
      } else {
        throw new Error(i18n.t('mockServices:errorMsgs.errorOnConfig'));
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('mockServices:errorMsgs.failedToUpgradeMockService', { name: mockService.name }),
          errorMsg,
        ),
      );
    } finally {
      //Hide loading icon and Refresh list of virtual services and close the popup
      dispatch(hideUpgradeDropDownLoadingAction());
      dispatch(getMockServiceListThunk());
      dispatch(closeUpgradeMockModalAction());
    }
  };
};

/**
 * Calls mock upgrade for bulk virtual services under location
 */
export const upgradeAllMockServicesUnderLocationThunk: any = (
  mockService: MockService,
  credentials?: BlazemeterCredentials,
  updateConfig?: UpdateConfig,
): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any) => {
    const shipId = mockService.shipId;
    const harborId = mockService.harborId;
    const locId = getLocationId(harborId, shipId);
    let mockIds;
    try {
      //Configure the virtual service
      MixpanelService.track(RECONFIURE_MOCK_SERVICE(mockService), true);

      let params = null;
      if (credentials) {
        params = createCredentialParameters(credentials, getState);
      }

      if (!_.isNil(updateConfig)) {
        params = {
          ...params,
          keepBlazeData: !_.isNil(updateConfig.keepBlazeData) ? updateConfig.keepBlazeData : undefined,
        };
      }
      const mocks: MockService[] = getState().mockservices.dataList;

      const filteredMs = mocks.filter(
        fm =>
          fm.status === 'RUNNING' &&
          fm.metadata &&
          fm.metadata.warnings &&
          fm.metadata.warnings.upgradeAvailable === true,
      );
      mockIds = filteredMs.map(mock => mock.id);

      dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });

      const configureResponse = await axios.get(`/workspaces/{workspaceId}/locations/${locId}/upgrade`, {
        headers: cacheControlHeaders,
        params: params,
      });
      if (
        !configureResponse ||
        !configureResponse.data ||
        !configureResponse.data.result ||
        !configureResponse.data.result.trackingId
      ) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      const trackingId = configureResponse.data.result.trackingId;

      // Poll for up to ~10 minutes (600 retry attempts with 1 second timeout between each)
      // until the virtual service is configured
      await pollTrackingService(trackingId);

      //Wait a second before we fetch newly created object from backend
      await setTimeout(() => {
        console.log('Wating before virtual service is updated by backend.');
      }, 1000);
      const configuredServiceResponse = await axios.get(`/workspaces/{workspaceId}/ui/service-mocks`, {
        headers: cacheControlHeaders,
        params: params,
      });

      if (configuredServiceResponse.status === 200) {
        if (configuredServiceResponse.data.result) {
          const mockServices = configuredServiceResponse.data.result as MockService[];
          if (!isUpgradeMockAvailableFromListOfMocks(mockServices)) {
            dispatch(showToastNotification(ToastType.Success, i18n.t('mockServices:successMsgs.upgradeAllSuccessMsg')));
            dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });
          } else {
            dispatch(
              showToastNotification(ToastType.Danger, '', i18n.t('mockServices:errorMsgs.failedToUpgradeMockServices')),
            );
            dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });
          }
        } else {
          dispatch(
            showToastNotification(ToastType.Danger, '', i18n.t('mockServices:errorMsgs.failedToUpgradeMockServices')),
          );
          dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });
        }
      } else {
        dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });
        throw new Error(i18n.t('mockServices:errorMsgs.errorOnConfig'));
      }
    } catch (err) {
      const errorMsg = _.get(err, 'response.data.error');
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('mockServices:errorMsgs.failedToUpgradeMockServices'),
          errorMsg.toString(),
        ),
      );
      if (mockIds) {
        dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });
      }
    } finally {
      //Hide loading icon and Refresh list of virtual services and close the popup
      dispatch(hideUpgradeDropDownLoadingAction());
      dispatch(getMockServiceListThunk());
      dispatch(closeUpgradeMockModalAction());
    }
  };
};

/*Add customized toast body */
const getSuccessToastBody = (mockServiceName?: string) => {
  return (
    <>
      <span
        className="mr-2 ml-2"
        dangerouslySetInnerHTML={{
          __html: i18n.t('mockServices:successMsgs.upgradeSuccessMsg', {
            name: mockServiceName,
          }),
        }}
      ></span>
    </>
  );
};

/**
 * Discards local changes for virtual service draft
 * @param mockService
 */
export const discardLocalChanges: any = (mockService: MockService): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    if (mockService.isDraft !== true) {
      new Error('Not a draft, cannot discard changes');
    }
    dispatch({
      type: actionTypes.SET_IN_PROGRESS,
      serviceMockId: mockService.id,
      inProgress: true,
      metaData: { inProgressAction: 'MERGE' },
    });

    try {
      await axios.delete(`/workspaces/{workspaceId}/service-mock-drafts/${mockService.id}`);

      // Check, if there was also original attached to this draft
      if (mockService.serviceMockId !== undefined) {
        // Now we need to revert original back
        const original = (await axios.get(`/workspaces/{workspaceId}/service-mocks/${mockService.serviceMockId}`)).data
          .result;
        dispatch(swapDraftAction(mockService.id, original, false, true));
        //Done with changes
        dispatch({
          type: actionTypes.SET_IN_PROGRESS,
          serviceMockId: original.id,
          inProgress: false,
          metaData: { inProgressAction: 'MERGE' },
        });
        dispatch(
          showToastNotification(
            ToastType.Success,
            'Local Changes Reverted',
            'Your local changes have been successfully reverted.',
          ),
        );

        //Reload Assets for template after changes are discarded
        dispatch(getAssetsForMockService(original));
      } else {
        // If it does not have any original, just delete the object from redux
        dispatch({
          type: actionTypes.DELETE_MOCK_SERVICE,
          payload: mockService,
        });
        dispatch(showToastNotification(ToastType.Success, 'Draft has been successfully deleted.'));
      }
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot Discard Local changes'));
      dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: mockService.id });
    } finally {
      //Reset detail with cert assets, so they are going to be re-loaded
      dispatch(setAssetBundle(null));
    }
  };
};

// TODO - break into more actions with less parameters
/**
 * Swaps ServiceMock and Draft and vice versa.
 * @param id - id of the object to be replaced, either MockService id or Draft id
 * @param mockService - virtual service
 * @param isDraft - marks object as draft
 * @param mergeAllFields - if set to true, all fields in the mockService will be merged. Default is true. If false, only ids are swapped.
 */
export const swapDraftAction = (id: number, mockService: MockService, isDraft: boolean, mergeAllFields?: boolean) =>
  action(actionTypes.SWAP_DRAFT, {
    idToReplace: id,
    replacement: mockService,
    isDraft: isDraft,
    mergeAllFields: mergeAllFields,
  });

export const deleteMockServiceItemThunk: ActionCreator<ThunkAction<
  any,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (mockService: MockService): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      //If we are working with draft, delete draft
      if (mockService.isDraft) {
        // Delete draft
        await axios.delete(`/workspaces/{workspaceId}/service-mock-drafts/${mockService.id}`, {});

        // Delete also original, if there is one available
        if (mockService.serviceMockId) {
          await axios.delete(`/workspaces/{workspaceId}/service-mocks/${mockService.serviceMockId}`, {});
        }
      } else {
        await axios.delete(`/workspaces/{workspaceId}/service-mocks/${mockService.id}`, {});
      }

      dispatch(
        showToastNotification(
          ToastType.Success,
          i18n.t('mockServices:successMsgs.deleteSuccessMsg', { name: mockService.name }),
        ),
      );
      dispatch({
        type: actionTypes.DELETE_MOCK_SERVICE,
        payload: mockService,
      });
    } catch (err) {
      const errorMsg = _.get(err, 'response.data.error');
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('mockServices:errorMsgs.failedToDeleteMockService', { name: mockService.name }),
          errorMsg,
        ),
      );
    } finally {
      dispatch(checkIfMockServiceExistInWorkspaceThunk());
    }
    MixpanelService.track(DELETE_MOCK_SERVICE(mockService.type), true);
  };
};

export const stopMockServiceThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MockServiceActionTypes>> = (
  mockService: MockService,
  credentials?: BlazemeterCredentials,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any) => {
    let mockServiceId: number;
    try {
      dispatch(toggleMockServiceInAction(mockService.id, { inProgressAction: 'STOP' }));

      let credentialParams = null;
      if (credentials) {
        credentialParams = createCredentialParameters(credentials, getState);
      }
      if (mockService.serviceMockId === undefined) {
        mockServiceId = mockService.id;
      } else {
        mockServiceId = mockService.serviceMockId;
      }

      //Create UI tracking
      const tracking: Tracking = {
        entityId: mockServiceId,
        action: 'STOP',
        entityType: 'MOCK_SERVICE',
      };
      dispatch(addTracking(tracking));

      const response = await axios.get(`/workspaces/{workspaceId}/service-mocks/${mockServiceId}/stop`, {
        params: credentialParams,
      });

      MixpanelService.track(STOP_MOCK_SERVICE(mockService.type), true);
      // track stopping of the service
      const trackingId = response.data.result.trackingId;
      await pollTrackingService(trackingId);
      let stoppedServiceResponse;

      // verify ms still exists in the list, otherwise it was deleted while stopping process was running
      const msPresent = _.find(
        getState().mockservices.dataList,
        (ms: MockService) => ms.id === mockServiceId || ms.serviceMockId === mockServiceId,
      );

      if (_.isNil(msPresent)) {
        // and then it shouldn't continue with the next steps
        return;
      }
      if (mockService.isDraft && mockService.id !== mockService.serviceMockId) {
        stoppedServiceResponse = await axios.get(`/workspaces/{workspaceId}/service-mock-drafts/${mockService.id}`, {
          headers: cacheControlHeaders,
        });
      } else {
        const mockServiceToStop = mockService.isDraft ? mockService.serviceMockId : mockService.id;
        stoppedServiceResponse = await axios.get(`/workspaces/{workspaceId}/service-mocks/${mockServiceToStop}`, {
          headers: cacheControlHeaders,
        });
      }

      const savedMockService: MockService = stoppedServiceResponse.data.result as MockService;

      // These two fields are missing from backend, we need to add them from existing ui mock servic object
      savedMockService.isDraft = mockService.isDraft;
      savedMockService.serviceMockId = mockService.serviceMockId;

      if (response.status === 200) {
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('mockServices:successMsgs.stopSuccessMsg', { name: mockService.name }),
          ),
        );
      }

      dispatch({
        type: actionTypes.STOP_MOCK_SERVICE,
        payload: {
          tempId: mockService.id,
          newMockService: savedMockService,
        },
      });
      dispatch(getMockServiceListThunk());
    } catch (err) {
      if (err.response && _.get(err.response, 'data.error')) {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('mockServices:errorMsgs.failedToStopMockService', { name: mockService.name }),
            _.get(err.response, 'data.error'),
          ),
        );
      } else {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('errorMsgs.networkProblem'),
            i18n.t('mockServices:errorMsgs.failedToStopMockService', { name: mockService.name }),
          ),
        );
      }
      dispatch(getMockServiceListThunk());
    } finally {
      dispatch(removeTracking({ type: 'MOCK_SERVICE', id: mockService.id }));
    }
  };
};

export const mockServiceChanged = (mockService: MockService) => {
  return {
    type: actionTypes.MOCK_SERVICE_CHANGED,
    payload: mockService,
  };
};

/**
 * Gets currently expanded virtual service.
 */
export const getExpandedMockService = () => {
  const expandedMsId = (store.getState() as ApplicationState).mockservices.expandedMockServiceId;
  if (_.isNil(expandedMsId)) {
    return null;
  }
  return (store.getState() as ApplicationState).mockservices.dataList.find(ms => expandedMsId === ms.id);
};

export const applyMockServiceTemplate: any = (mockService: MockService, template: MockServiceTemplate): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const updatedMockService: any = Object.assign({}, mockService, { original: null });
    updatedMockService.templateId = template.id;
    updatedMockService.mockServiceTransactions = template.mockServiceTransactions;
    updatedMockService.description = template.description;
    updatedMockService.liveSystemHost = template.liveSystemHost;
    updatedMockService.liveSystemPort = template.liveSystemPort;
    updatedMockService.thinkTime = template.thinkTime;
    updatedMockService.noMatchingRequestPreference = template.noMatchingRequestPreference;
    updatedMockService.metaData.dataChangedTs = new Date().getTime();
    updatedMockService.appliedTemplateId = template.isDraft ? template.serviceMockTemplateId : template.id;
    updatedMockService.isTemplateActive = true;
    updatedMockService.dataSettings = template.dataSettings;
    updatedMockService.responseDelay = template.responseDelay;
    updatedMockService.configurationId = template.configurationId;
    updatedMockService.proxy = template.proxy;
    updatedMockService.priorityMode = template.priorityMode;
    updatedMockService.brokerConfig = template.brokerConfig;
    updatedMockService.httpRunnerEnabled = template.httpRunnerEnabled;
    updatedMockService.messagingRunnerEnabled = template.messagingRunnerEnabled;
    updatedMockService.messagingProtocol = template.messagingProtocol;
    updatedMockService.mockServiceRecordings = template.mockServiceRecordings;

    if (isOplLocation(updatedMockService.harborId, updatedMockService.shipId)) {
      updatedMockService.httpRunnerOverrideKeystore = template.httpRunnerOverrideKeystore;
      updatedMockService.httpRunnerOverrideTruststore = template.httpRunnerOverrideTruststore;
    }

    //Get assets for this template and apply them to the virtual service
    let rsp;
    try {
      if (template.isDraft) {
        rsp = (await acApi.templateDrafts.getById(template.id)).data.result;
      } else {
        rsp = (await acApi.templates.getById(template.id)).data.result;
      }
    } catch (e) {
      const errMsg = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Warning, 'Cannot apply Certificates to Virtual Service', errMsg));
    }
    updatedMockService.assets = _.get(rsp, 'assets');
    updatedMockService.configKeyAssets = _.get(rsp, 'configKeyAssets');

    // Assign assets to the virtual service
    const isOplLoc = isOplLocation(updatedMockService.harborId, updatedMockService.shipId);
    const assetsReFetched = await getAssetsForMockServiceFromTemplate(template, !isOplLoc);
    updatedMockService.assets = assetsReFetched.assets;
    updatedMockService.configKeyAssets = assetsReFetched.configKeyAssets;
    updatedMockService.metaData.dataChangedTs = new Date().getTime();
    updatedMockService.mockServiceTransactions = assetsReFetched.mockServiceTransactions;
    updatedMockService.mockServiceRecordings = assetsReFetched.mockServiceRecordings;

    // Override replicas, if needed
    const isScalable: boolean = isLocationScalable(mockService.harborId, mockService.shipId);
    if (isScalable === true) {
      updatedMockService.replicas = template.replicas;
    }

    // Send patch to backend
    if (!updatedMockService.isDraft) {
      await dispatch(createDraft(updatedMockService));
    }

    try {
      await axios.patch('/workspaces/{workspaceId}/service-mock-drafts/' + updatedMockService.id, updatedMockService);
      dispatch(mockServiceChanged(updatedMockService));

      //Reload the shuttle
      await dispatch(reloadTransactionShuttle(updatedMockService));

      //Reload assets in detail
      await dispatch(getAssetsForMockService(updatedMockService));

      //Close the detail of mock service
      const expandedMockServiceId: any = getState().mockservices.expandedMockServiceId;
      if (expandedMockServiceId === updatedMockService.id) {
        dispatch(toggleExpandedMockServiceRow(updatedMockService.id));
      }

      dispatch(
        showToastNotification(
          ToastType.Success,
          '',
          i18n.t('mockServices:successMsgs.templateAppliedSuccess', {
            template: template.name,
            service: mockService.name,
          }),
        ),
      );
    } catch (error) {
      const msg = _.get(error, 'response.data.error', '');
      dispatch(showToastNotification(ToastType.Danger, 'Apply template action failed', msg));
    }
  };
};

export const mockServiceTemplateChanged = (
  mockService: MockService,
  mockServicTemplate: MockServiceTemplate,
  overrideReplicas: boolean,
) => {
  return {
    type: actionTypes.MOCK_SERVICE_TEMPLATE_CHANGED,
    payload: {
      serviceMockId: mockService.id,
      newTemplate: mockServicTemplate,
      overrideReplicas: overrideReplicas,
    },
  };
};

/**
 * When transactions in mock-service change.
 * @param serviceMockId
 * @param transactionsIds
 */
export const mockServiceTransactionsChanged = (serviceMockId: number, transactionsIds: number[]) => {
  return {
    type: actionTypes.MOCK_SERVICE_TRANSACTIONS_CHANGED,
    payload: {
      serviceMockId: serviceMockId,
      mockServiceTransactions: transactionsIds,
    },
  };
};

export const mockServiceTransactionContentChanged = (serviceMockId: number, updatedTransaction: Transaction) => {
  return {
    type: actionTypes.MOCK_SERVICE_TRANSACTION_CONTENT_CHANGED,
    payload: {
      serviceMockId: serviceMockId,
      updatedTransaction: updatedTransaction,
    },
  };
};

export const getLocationsThunk: ActionCreator<ThunkAction<
  any,
  ApplicationState,
  null,
  MockServiceActionTypes
>> = (): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      const response = await axios.get(`/workspaces/{workspaceId}/locations`, {
        headers: cacheControlHeaders,
      });
      dispatch({
        type: actionTypes.GET_LOC,
        payload: response.data.result,
      });
    } catch (err) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.networkProblem'),
          i18n.t('mockServices:errorMsgs.cannotLoadLocations'),
        ),
      );
    }
  };
};

/**
 * Checks if the blaze data dialog should be displayed for the virtual service during the update.
 * Returns true if update dialog should be shown.

 * @param mockService
 */

export const isReconfigureBlazeDataDialogShown = async (mockService: MockService) => {
  // If current object is draft, we need to fetch the "original"
  // virtual service object and also check if it is using blaze data templates
  if (mockService.isDraft) {
    try {
      const ms: MockService = (await acApi.mockServices.getById(mockService.serviceMockId)).data.result;
      const isDataSettingsWithoutChange = _.isMatch(ms.dataSettings, mockService.dataSettings);

      // Dialog is shown if "original" virtual service contains blaze-data enabled transactions
      // and there was no change in the iteration overrides (in such case, we do not show the dialog and
      // do the update of blaze data automatically
      if (Boolean(ms.metadata?.badges?.CONTAINS_EXPRESSIONS).valueOf() && isDataSettingsWithoutChange) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
    }
  }

  return hasBlazeDataTemplates(mockService);
};

/**
 * Checks, if virtual service has some blaze data.
 * @param ms
 */
export const hasBlazeDataTemplates = async (ms: MockService) => {
  if (!ms) {
    return false;
  }

  try {
    const msResult: MockService = await getCurrentMockServiceOrDraft(ms);
    return new Boolean(msResult.metadata?.badges?.CONTAINS_EXPRESSIONS).valueOf();
  } catch (error) {
    return false;
  }
};

/**
 * Get current representation of virtual service / draft from backend by its id. The DTO obtained from backend
 * is different from the one obtained from ui controller.
 * @param ms
 */
export const getCurrentMockServiceOrDraft = async (ms: MockService) => {
  const endpoint = ms.isDraft ? 'service-mock-drafts' : 'service-mocks';
  const url = `/workspaces/{workspaceId}/${endpoint}/${ms.id}`;
  return (await axios.get(url)).data.result;
};

/**
 * Creates draft from an existing Virtual Service object. Returns draft
 */
export const createDraft = (mockService: MockService): any => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    //Show the in progress message and mark virtual service as draft
    await dispatch(swapDraftAction(mockService.id, mockService, true, false));
    dispatch({
      type: actionTypes.SET_IN_PROGRESS,
      inProgress: true,
      serviceMockId: mockService.id,
      metaData: { inProgressAction: null },
    });

    try {
      const draft = (await axios.post(`/workspaces/{workspaceId}/service-mock-drafts?serviceMockId=${mockService.id}`))
        .data.result;

      // Replace virtual service with draft return from backend
      dispatch(swapDraftAction(mockService.id, draft, true, false));

      // Change id of virtual service to be updated by draft id(it was replace already in the store)
      mockService.id = draft.id;
      mockService.serviceMockId = draft.serviceMockId;
      mockService.isDraft = true;

      dispatch(mockServiceChanged(mockService));

      return mockService;
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, 'Could not create Draft', error.toLocaleString()));
      return null;
    } finally {
      // Hide the in progress message
      dispatch({ type: actionTypes.SET_IN_PROGRESS, serviceMockId: mockService.id, inProgress: false });
    }
  };
};

/**
 * Updates virtual service object.
 * @param mockService
 * @param fieldToUpdate
 */
export const mockServicePatchAction: any = (
  mockService: MockService,
  fieldToUpdate?: string,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    const updatedMockService = _.cloneDeep(mockService);
    updatedMockService.metaData.isDirty = isDirty(updatedMockService);
    // Do a copy the state of the object in the store, before we change it
    const prevMockService = getState().mockservices.dataList.find((item: any) => {
      return _.cloneDeep(item.id === updatedMockService.id);
    });
    if (prevMockService === undefined) {
      return updatedMockService;
    }

    //Change the store
    dispatch(mockServiceChanged(updatedMockService));

    // If we are not working with a draft, create it beforehand
    if (mockService.isDraft !== true) {
      await dispatch(createDraft(updatedMockService));
    }

    // Create diff from the previous state of the object in the redux store
    // Or if a field is specified, it uses directly only that field
    // The result is object with attributes which had changed
    let msPatchDto: any =
      fieldToUpdate !== undefined
        ? { [fieldToUpdate]: updatedMockService[fieldToUpdate] }
        : getDifference(getCleanMockService(updatedMockService), prevMockService);

    // Sanitize values from a result of the patch diff
    msPatchDto = sanitizeMockPatchDto(msPatchDto, updatedMockService);

    // Do not send a patch request when a patch is empty
    if (_.isEmpty(msPatchDto)) {
      return updatedMockService;
    }

    // Propagate changes to Backend and Redux store
    try {
      msPatchDto.isTemplateActive = false; //since is an update in any of the fields
      await axios.patch('/workspaces/{workspaceId}/service-mock-drafts/' + updatedMockService.id, msPatchDto);
    } catch (error) {
      let message = error.toLocaleString();
      if (error.response !== 'undefined') {
        message = error.response.data.error;
      }
      dispatch(showToastNotification(ToastType.Danger, 'Could not update Virtual Service', message));
    } finally {
      // Expand virtual service item
      dispatch(expandMockServiceRow(updatedMockService.id));

      // If we changed service id or reset transaction assignment
      // reload panes in the detail shuttle
      const isServiceChanged: boolean =
        prevMockService.serviceId !== updatedMockService.serviceId &&
        getState().mockservices.expandedMockServiceId === updatedMockService.id;

      const isTransactionSelectionChanged: boolean =
        prevMockService.mockServiceTransactions !== undefined &&
        updatedMockService.mockServiceTransactions !== undefined &&
        prevMockService.mockServiceTransactions.length !== updatedMockService.mockServiceTransactions.length;

      const isLocationChanged = prevMockService.harborId !== updatedMockService.harborId;
      const isPriorityModeChanged = prevMockService.priorityMode !== updatedMockService.priorityMode;
      const activeRunner = getActiveRunner(getState);

      //If we changed service or transaction selection, reload the whole shuttle
      if (isServiceChanged || isTransactionSelectionChanged) {
        dispatch(reloadTransactionShuttle(updatedMockService, activeRunner));
      }
      //When priority mode changes, and we are in draft mode - refresh only right shuttle pane
      else if (isPriorityModeChanged) {
        dispatch(fetchTransactionsForShuttlePane(updatedMockService, 'RIGHT', activeRunner));
      }
      //If we changed location, reset the detail (we might have unsupported tab open)
      else if (isLocationChanged) {
        dispatch(mockServiceDetailTabChanged('main'));
      }

      //If we just changed matching to 404, refresh list of certificates
      if (
        updatedMockService.noMatchingRequestPreference !== prevMockService.noMatchingRequestPreference &&
        updatedMockService.noMatchingRequestPreference === 'return404'
      ) {
        dispatch(getAssetsForMockService(updatedMockService));
      }
    }
    return updatedMockService;
  };
};

const getActiveRunner = (getState: any) => {
  return getState().mockServiceDetails.runnerTab === 'httpRunner' ? 'HTTP' : 'MESSAGING';
};

/**
 * Clears all unnecessary metadata from virtual service object, which are not needed for the patch request.
 * @param mockService
 */
export const getCleanMockService = (mockService: MockService) => {
  const pureMockServiceDto: any = { ...mockService };

  delete pureMockServiceDto.metaData;
  delete pureMockServiceDto.original;
  delete pureMockServiceDto.id;
  delete pureMockServiceDto.isDraft;
  delete pureMockServiceDto.isNew;
  delete pureMockServiceDto.updated;
  delete pureMockServiceDto.created;
  delete pureMockServiceDto.serviceMockId;

  return pureMockServiceDto;
};

/**
 * Merge virtual service.
 * @param mockId
 * @param updateConfig
 */
async function invokeMergeAndSwapDraft(mockId: number, updateConfig?: UpdateConfig) {
  let requestParams = {};

  //If templates are affected during the merge
  if (!_.isNil(updateConfig)) {
    requestParams = {
      params: {
        updateTemplate: !!updateConfig.updateAppliedTemplate ? updateConfig.updateAppliedTemplate : false,
      },
    };
  }
  const response: BlazeMeterApiResponse = (
    await axios.post(`/workspaces/{workspaceId}/service-mock-drafts/${mockId}/merge`, null, requestParams)
  ).data;
  //Get object from the response and replace it with our draft in the store
  const savedMockService: MockService = response.result;
  store.dispatch(swapDraftAction(mockId, savedMockService, false, true));
  //store.dispatch(mockServiceChanged(savedMockService));

  //If we just changed the templates by this virtual service, we should re-fetch them to see the changes
  if (!_.isNil(updateConfig) && !!updateConfig.updateAppliedTemplate) {
    store.dispatch(getMockServiceTemplates() as any);
    store.dispatch(collapseAllTemplates() as any);
  }
  return savedMockService;
}

/**
 * Update mock service stage objects during the tracking
 */
export const updateMockServiceStages = (data: any, getState: any, dispatch: any) => {
  const trackingDtos: ServiceMockTrackingDto[] = _.get(data, 'data.result.data.serviceMockTrackingDtos');
  if (_.isNil(trackingDtos)) {
    return;
  }

  const mockServiceList = getState().mockservices.dataList;
  let globalUpdateNeeded = false;

  // For each tracked mock service, find the item in the list and check
  // if it needs to be updated (= stage is different from the one in the tracking)
  const stageUpdate = _.map(trackingDtos, item => {
    let msNeedsUpdate = false;
    const ms = _.find(mockServiceList, (ms: MockService) => ms.id === item.serviceMockId);
    if (ms) {
      const msStage = _.get(ms, 'metaData.trackingStatus');
      msNeedsUpdate = msStage !== item.trackingDto.data.stage;

      //Set global update
      if (msNeedsUpdate) {
        globalUpdateNeeded = true;
      }
    }

    return {
      id: item.serviceMockId,
      stage: item.trackingDto.data.stage,
      needsUpdate: msNeedsUpdate,
    };
  });

  //There is at least one MS with state change, so we do not re-render each time
  if (globalUpdateNeeded) {
    const filteredStageList = _.filter(stageUpdate, item => item.needsUpdate === true);
    dispatch(bulkMockServiceStageUpdate(filteredStageList));
  }
};

export const dispatchPortInUseWarningMsgs = (warnings: string[]) => {
  if (warnings && warnings.length == 1 && warnings[0]) {
    dispatchWarningMessage(warnings[0].toString());
  } else if (warnings && warnings.length > 1) {
    const warnMsg: string = warnings.join('. \n');
    dispatchWarningMessage(warnMsg);
  }
};

export const performBulkAction = (
  mocks: MockService[],
  action: MockServiceBulkAction,
  unSelectAll: any,
  credentials?: BlazemeterCredentials,
): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>, getState: any) => {
    let mockIds = mocks.map(mock => mock.id);

    //Check if the number of selected records does not exceed limit
    const limit = getBulkLimit();
    let limitApplied = false;
    if (mocks.length > limit) {
      mockIds = mockIds.slice(0, limit - 1);
      limitApplied = true;
    }

    try {
      //Show the in progress message
      dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds, actionType: action });

      const savedMocks: MockService[] = [];
      for (const mock of mocks) {
        if (mock.isDraft) {
          try {
            const savedMockService = await invokeMergeAndSwapDraft(mock.id);
            mockIds = mockIds.filter(mockId => mockId !== mock.id);
            mockIds.push(savedMockService.id);
            savedMocks.push(savedMockService);
          } catch (error) {
            throw new Error(
              `${i18n.t('mockServices:errorMsgs.failedToUpdateMockService', { name: mock.name })}` +
                _.get(error, 'response.data.error', ''),
            );
          }
        }
      }

      mocks = mocks.filter(mock => mockIds.includes(mock.id));
      mocks.push(...savedMocks);

      let deployResponse;
      if (isPrivateCloud(getState().blazeUserConfig.blazeUserConfig)) {
        deployResponse = await axios.patch<BlazeMeterApiResponse>(`/workspaces/{workspaceId}/service-mocks`, {
          action: action,
          mockServiceIds: mockIds,
          credentialsId: credentials ? credentials.id : '',
          username: credentials ? btoa(credentials.credentials.username) : '',
          password: credentials ? btoa(credentials.credentials.password) : '',
        });
      } else {
        deployResponse = await axios.patch<BlazeMeterApiResponse>(`/workspaces/{workspaceId}/service-mocks`, {
          action: action,
          mockServiceIds: mockIds,
          credentialsId: credentials ? credentials.id : '',
        });
      }

      //If list of virtual services was truncated
      if (limitApplied === true) {
        confirm({
          size: 'sm',
          confirmation: (
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t('mockServices:mockServicesTable.bulkActionDialog.body', { limit: limit }),
              }}
            ></span>
          ),
          yesOptionTitle: i18n.t('common:confirmations.ok'),
          yesOptionVariant: 'primary',
          isCancelHidden: true,
          title: i18n.t('mockServices:mockServicesTable.bulkActionDialog.title'),
        });
      }

      let errors = _.get(deployResponse, 'data.error');
      if (!_.isEmpty(errors)) {
        throw new Error(errors);
      }

      if (
        !deployResponse ||
        !deployResponse.data ||
        !deployResponse.data.result[0] ||
        !deployResponse.data.result[0].trackingDto.trackingId
      ) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }

      const trackingId = deployResponse.data.result[0].trackingDto.trackingId;

      // Poll for up to ~10 minutes (600 retry attempts with 1 second timeout between each)
      // until the virtual service is deployed
      let trackingResult: any;

      //Define callback for start event
      if (action === 'START') {
        trackingResult = await pollTrackingService(trackingId, POLL_NUM_OF_RETRIES, POLL_INTERVAL_MS, data =>
          updateMockServiceStages(data, getState, dispatch),
        );
      } else {
        trackingResult = await pollTrackingService(trackingId, POLL_NUM_OF_RETRIES, POLL_INTERVAL_MS);
      }
      errors = _.get(trackingResult, 'data.result.errors');
      const warnings = _.get(trackingResult, 'data.result.warnings');
      const trackingDtos = _.get(trackingResult, 'data.result.data.serviceMockTrackingDtos');
      if (!_.isEmpty(errors) && errors.length === trackingDtos.length) {
        // Indicate all the mocks failed. Show a generic error message.
        throw new Error(errors[0]);
      }

      if (!_.isEmpty(errors)) {
        let partialSuccessMessage = 'mockServices:errorMsgs.deployBulkStartPartialSuccessMsg';
        if (action === 'STOP') {
          partialSuccessMessage = 'mockServices:errorMsgs.deployBulkStopPartialSuccessMsg';
        }
        const errorMessage: string = getBulkPartialFailureErrorMessage(errors, trackingDtos, action);

        dispatchPortInUseWarningMsgs(warnings);
        dispatch(showToastNotification(ToastType.Danger, `${i18n.t(partialSuccessMessage)}`, errorMessage));
      } else {
        const successKey = mocks.length === 1 ? 'deploySingleStartSuccessMsg' : 'deployBulkStartSuccessMsg';
        const successStopKey = mocks.length === 1 ? 'deploySingleStopSuccessMsg' : 'deployBulkStopSuccessMsg';

        const successMessagePrefix = 'mockServices:successMsgs.';
        let successMessage: string;

        if (action === 'STOP') {
          successMessage = successMessagePrefix + successStopKey;
        } else {
          successMessage = successMessagePrefix + successKey;
        }
        dispatchPortInUseWarningMsgs(warnings);
        dispatch(showToastNotification(ToastType.Success, `${i18n.t(successMessage, { count: mocks.length })}`));
      }

      dispatch(getMockServiceListThunk());

      //Hide the in progress message
      dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });
    } catch (err) {
      dispatch({ type: actionTypes.BULK_TOGGLE_IN_PROGRESS, serviceMockIds: mockIds });
      dispatch(getMockServiceListThunk());
      let defaultErrorMessage = i18n.t('mockServices:errorMsgs.cannotStartMockServices');
      if (action === 'STOP') {
        defaultErrorMessage = i18n.t('mockServices:errorMsgs.cannotStopMockServices');
      }
      const errorMessage = err && !_.isEmpty(err.message) ? err.message : defaultErrorMessage;
      dispatch(showToastNotification(ToastType.Danger, '', `${i18n.t(errorMessage)}`));
    } finally {
      unSelectAll();
    }
  };
};

export const bulkDeleteAction = (mocks: MockService[]): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    try {
      dispatch({ type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: true } });
      const response = await axios.patch<BlazeMeterApiResponse>(`/workspaces/{workspaceId}/service-mocks`, {
        action: 'DELETE',
        mockServiceIds: mocks.map(mock => mock.id),
      });
      const error = _.get(response, 'data.error');
      if (!_.isEmpty(error)) {
        throw new Error(error);
      }
      if (!response || !response.data || !response.data.result[0] || !response.data.result[0].trackingDto.trackingId) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      try {
        const trackingId = response.data.result[0].trackingDto.trackingId;
        const trackingResult = await pollTrackingService(trackingId);
        const errors = _.get(trackingResult, 'data.result.errors');

        if (!_.isEmpty(errors)) {
          // Indicate all the mocks deletion got failed. Show a generic error message.
          throw new Error(errors[0]);
        }
        MixpanelService.track(MOCK_SERVICE_BULK_OPERATION('delete', mocks.length));
        dispatch({ type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: false } });
        const successKey = mocks.length > 1 ? 'bulkDeleteMultiple' : 'bulkDeleteSingle';
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('mockServices:successMsgs.' + successKey, { count: mocks.length }),
          ),
        );
      } catch (e) {
        dispatch({ type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: false } });
        const errorMsg = getErrorMessage(e);
        dispatch(
          showToastNotification(
            ToastType.Danger,
            `${i18n.t('mockServices:errorMsgs.cannotDeleteMockServices')}  ${errorMsg}`,
            '',
          ),
        );
      } finally {
        dispatch(checkIfMockServiceExistInWorkspaceThunk());
      }
    } catch (e) {
      dispatch({ type: actionTypes.LOADING_MOCK_SERVICES_LIST, payload: { displayLoading: false } });
      const errorMsg = getErrorMessage(e);
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('mockServices:errorMsgs.cannotDeleteMockServices')}  ${errorMsg}`,
        ),
      );
    } finally {
      dispatch(getMockServiceListThunk());
    }
  };
};

/**
 * If virtual service is blaze data enabled, we will additionally check the blaze data model complexity
 * and if exceeds a limit there will be a warning alert shown to the user during the tracking phase.
 * @param mockService
 */
export const checkBlazeDataComplexity = async (mockService: MockService) => {
  if ((await hasBlazeDataTemplates(mockService)) === false) {
    return;
  }

  const activeWorkspaceId = getActiveWorkspaceId();
  const dataModel = await getDataModelForService(mockService.serviceId);

  if (!dataModel || _.isNil(dataModel.id)) {
    return;
  }

  const overrides = _.get(mockService, 'dataSettings');
  const complexity = await getModelComplexity(activeWorkspaceId, dataModel?.id, overrides);
  const isOverThreshold = isComplexityOverThreshold(_.get(complexity, 'result.totalCount', 0));
  if (isOverThreshold) {
    const msg = i18n.t('mockServices:warnMsgs.blazeDataLongProcessing', { name: mockService.name });
    store.dispatch(showToastNotification(ToastType.Warning, '', msg));
  }
};

export const openUpgradeMockModalAction = () => action(actionTypes.OPEN_UPGRADE_MOCK_SERVICE_MODAL, {});
export const closeUpgradeMockModalAction = () => action(actionTypes.CLOSE_UPGRADE_MOCK_SERVICE_MODAL, {});
export const showUpgradeDropDownLoadingAction = () => action(actionTypes.SHOW_UPGRADE_DROPDOWN_LOADING, {});
export const hideUpgradeDropDownLoadingAction = () => action(actionTypes.HIDE_UPGRADE_DROPDOWN_LOADING, {});
